import type { FacetDef, FilterDict } from '../../components/list-controls/types';

export type DemoDocLifecycle = 'draft' | 'published';

export interface DemoDocument {
  id: string;
  typeLabel: string;
  typeId: string;
  title: string;
  preview: string;
  lifecycle: DemoDocLifecycle;
  ownerId: 'me' | 'other';
}

export const DEMO_DOCUMENTS: readonly DemoDocument[] = [
  {
    id: 'spec-onboarding',
    typeLabel: 'Spec',
    typeId: 'spec',
    title: 'Sidepanel onboarding spec',
    preview: 'Empty + first-run states for the inspector sidepanel…',
    lifecycle: 'draft',
    ownerId: 'me'
  },
  {
    id: 'prd-q3',
    typeLabel: 'PRD',
    typeId: 'prd',
    title: 'Q3 roadmap',
    preview: 'Themes, milestones, and cross-team dependencies for Q3…',
    lifecycle: 'published',
    ownerId: 'me'
  },
  {
    id: 'runbook-deploy',
    typeLabel: 'Runbook',
    typeId: 'runbook',
    title: 'Deploy pipeline runbook',
    preview: 'Release checklist, rollback steps, and on-call routing…',
    lifecycle: 'published',
    ownerId: 'other'
  },
  {
    id: 'adr-auth',
    typeLabel: 'ADR',
    typeId: 'adr',
    title: 'Auth provider decision',
    preview: 'Why Supabase Auth is the shared session layer for IDE + web…',
    lifecycle: 'draft',
    ownerId: 'other'
  }
];

export const DEMO_FACETS: readonly FacetDef[] = [
  {
    key: 'owner',
    label: 'Owner',
    options: [{ id: 'me', label: 'Me' }]
  },
  {
    key: 'type',
    label: 'Type',
    options: [
      { id: 'spec', label: 'Spec' },
      { id: 'prd', label: 'PRD' },
      { id: 'runbook', label: 'Runbook' },
      { id: 'adr', label: 'ADR' }
    ]
  },
  {
    key: 'lifecycle',
    label: 'Lifecycle',
    options: [
      { id: 'draft', label: 'Draft' },
      { id: 'published', label: 'Published' }
    ]
  }
];

export const EMPTY_FILTERS: FilterDict = {
  owner: [],
  type: [],
  lifecycle: []
};

export function filterDocuments(filters: FilterDict): DemoDocument[] {
  return DEMO_DOCUMENTS.filter((doc) => {
    const owners = filters.owner ?? [];
    if (owners.length > 0 && !owners.includes(doc.ownerId)) return false;

    const types = filters.type ?? [];
    if (types.length > 0 && !types.includes(doc.typeId)) return false;

    const lifecycles = filters.lifecycle ?? [];
    if (lifecycles.length > 0 && !lifecycles.includes(doc.lifecycle)) return false;

    return true;
  });
}

export function facetLabel(key: string): string {
  return DEMO_FACETS.find((f) => f.key === key)?.label ?? key;
}

export function valueLabel(facetKey: string, valueId: string): string {
  const facet = DEMO_FACETS.find((f) => f.key === facetKey);
  return facet?.options.find((o) => o.id === valueId)?.label ?? valueId;
}

export function nonEmptyFilterKeys(filters: FilterDict): string[] {
  return Object.keys(filters).filter((k) => (filters[k] ?? []).length > 0);
}
